// Hover picks on every pointer sample, which without a bounds tree is a triangle scan of the assembly.
// Trees are built in idle time: they are an optimisation, and the plain raycast path stays correct
// until they exist.

import * as THREE from 'three'
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh'

const MIN_TRIANGLES_FOR_TREE = 400
const BUILD_SLICE_MS = 8

let installed = false

export function enableAcceleratedPicking() {
  if (installed) return
  installed = true
  THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
  THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree
  THREE.Mesh.prototype.raycast = acceleratedRaycast
}

function triangleCount(geometry: THREE.BufferGeometry): number {
  const count = geometry.index
    ? geometry.index.count
    : (geometry.getAttribute('position')?.count ?? 0)
  return count / 3
}

type IdleDeadline = { timeRemaining: () => number; didTimeout: boolean }
type IdleScheduler = (cb: (deadline: IdleDeadline) => void) => number

function idleScheduler(): IdleScheduler {
  const native = (window as unknown as { requestIdleCallback?: IdleScheduler }).requestIdleCallback
  if (native) return native.bind(window)
  return (cb) =>
    window.setTimeout(() => cb({ timeRemaining: () => BUILD_SLICE_MS, didTimeout: true }), 1)
}

export function scheduleBoundsTrees(meshes: THREE.Mesh[]): () => void {
  const pending = meshes.filter(
    (m) => !m.geometry.boundsTree && triangleCount(m.geometry) >= MIN_TRIANGLES_FOR_TREE,
  )
  if (pending.length === 0) return () => {}

  const schedule = idleScheduler()
  let cancelled = false
  let cursor = 0

  const step = (deadline: IdleDeadline) => {
    if (cancelled) return
    const started = performance.now()
    while (cursor < pending.length) {
      const withinIdle = deadline.timeRemaining() > 1
      const withinSlice = performance.now() - started < BUILD_SLICE_MS
      if (!withinIdle && !withinSlice) break
      const geometry = pending[cursor++].geometry
      try {
        if (!geometry.boundsTree && geometry.getAttribute('position')) geometry.computeBoundsTree()
      } catch {
        // Leaves this mesh on the stock raycast path.
      }
    }
    if (cursor < pending.length) schedule(step)
  }

  schedule(step)
  return () => {
    cancelled = true
  }
}

export function disposeBoundsTrees(root: THREE.Object3D) {
  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh && obj.geometry?.boundsTree) obj.geometry.disposeBoundsTree()
  })
}
